import { Link, NavLink } from 'react-router-dom';
import { Box, ScrollArea, Text, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import { IconLayoutDashboard, IconMenu2 } from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';
import classes from './Sidebar.module.css';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon?: TablerIcon;
  end?: boolean;
  isActive?: boolean;
  testId?: string;
}

export interface SidebarNavGroup {
  title: string;
  items: SidebarNavItem[];
}

export interface SidebarProps {
  groups: SidebarNavGroup[];
  changeContextLabel?: string;
  changeContextPath?: string;
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  collapseLabel?: string;
  expandLabel?: string;
}

export function Sidebar({
  groups,
  changeContextLabel,
  changeContextPath = '/select-context',
  onNavigate,
  collapsed = false,
  onToggleCollapsed,
  collapseLabel = 'Collapse',
  expandLabel = 'Expand',
}: SidebarProps) {
  const toggleLabel = collapsed ? expandLabel : collapseLabel;

  const brandIcon = (
    <ThemeIcon
      size={36}
      radius="md"
      variant="gradient"
      gradient={{ from: 'blue', to: 'indigo', deg: 135 }}
      className={onToggleCollapsed ? classes.brandToggleIcon : undefined}
    >
      <IconMenu2 size={20} stroke={2.2} color="white" aria-hidden />
    </ThemeIcon>
  );

  return (
    <Box
      className={`${classes.root} ${collapsed ? classes.rootCollapsed : ''}`.trim()}
      data-testid="app-sidebar"
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <div className={classes.brand} data-testid="app-brand">
        {onToggleCollapsed ? (
          <Tooltip label={toggleLabel} withArrow position="right">
            <UnstyledButton
              className={classes.brandToggle}
              aria-label={toggleLabel}
              data-testid="sidebar-collapse-toggle"
              onClick={onToggleCollapsed}
            >
              {brandIcon}
            </UnstyledButton>
          </Tooltip>
        ) : (
          brandIcon
        )}
        {!collapsed ? <Text fw={800} c="white">MH BankStatement</Text> : null}
      </div>

      <ScrollArea className={classes.scroll} type="auto" scrollbars="y" px={collapsed ? 6 : 'sm'}>
        {groups.map((group) => (
          <div key={group.title} className={classes.navGroup}>
            {!collapsed ? <div className={classes.navGroupTitle}>{group.title}</div> : null}
            {group.items.map((item) => {
              const Icon = item.icon ?? IconLayoutDashboard;
              const link = (
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `${classes.navItem} ${collapsed ? classes.navItemCollapsed : ''} ${(item.isActive ?? isActive) ? classes.navItemActive : ''}`.trim()
                  }
                >
                  <Icon size={18} stroke={1.8} />
                  {!collapsed ? <Text size="sm" fw={500}>{item.label}</Text> : null}
                </NavLink>
              );

              if (!collapsed) {
                return <div key={item.to}>{link}</div>;
              }

              return (
                <Tooltip key={item.to} label={item.label} withArrow position="right">
                  <div className={classes.navItemWrap}>{link}</div>
                </Tooltip>
              );
            })}
          </div>
        ))}
      </ScrollArea>

      {changeContextLabel ? (
        <div className={classes.userFooter}>
          {collapsed ? (
            <Tooltip label={changeContextLabel} withArrow position="right">
              <UnstyledButton
                component={Link}
                to={changeContextPath}
                className={classes.contextLinkCollapsed}
                onClick={onNavigate}
                aria-label={changeContextLabel}
              >
                <IconLayoutDashboard size={18} stroke={1.8} />
              </UnstyledButton>
            </Tooltip>
          ) : (
            <Link to={changeContextPath} className={classes.contextLink} onClick={onNavigate}>
              {changeContextLabel}
            </Link>
          )}
        </div>
      ) : null}
    </Box>
  );
}
